import "bootstrap-icons/font/bootstrap-icons.css";
import { formatDateTime } from "../../../utils/datetime";
import "bootstrap/dist/css/bootstrap.min.css";
import { Brain, Edit2, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Form, ProgressBar } from "react-bootstrap";
import APICall from "../../../APICalls/APICall";


/* ================= COMPONENT ================= */

const IntentDomainContainer = () => {
  const [intentDomain, setIntentDomain] = useState([])
  const [activeIntentDomain, setActiveIntentDomain] = useState(null)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [intentDomainToDelete, setIntentDomainToDelete] = useState(null)

  const [loading, setLoading] = useState(true)

  /* ================= API LAYER ================= */

  const IntentDomainAPI = {
    fetchAll: async () => {
      const res = await APICall.getT("/intents/intent_domain")
      return res || []
    },

    create: async payload => {
      const res = await APICall.postT("/intents/intent_domain", payload)
      return res
    },

    update: async payload => {
      const res = await APICall.postT(
        `/intents/update_intent_domain/${payload.id}`,
        payload
      )
      return res
    },

    remove: async id => {
      await APICall.postT(`/intents/delete_intent_domain/${id}`)
      return true
    },
    train: async id => {
      return await APICall.postT(`/nlp/train/${id}`)
    },
    trainAll: async () => {
      return await APICall.postT(`/nlp/train_all`)
    },
    trainAllStatus: async () => {
      return await APICall.getT(`/nlp/train_all/status`)
    }
  }

  /* ================= FETCH ================= */

  const loadIntentDomain = useCallback(async () => {
    try {
      setLoading(true)
      const data = await IntentDomainAPI.fetchAll()
      setIntentDomain(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error("Failed to load intent Domains", err)
      setIntentDomain([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadIntentDomain()
  }, [loadIntentDomain])

  /* ================= ADD / EDIT ================= */

  const handleAdd = () => {
    setActiveIntentDomain({
      name: "",
      description: "",
      rasa_endpoint: "",
      auto_reload_enabled: true,
      confidence_threshold: 60
    })
    setIsModalOpen(true)
  }

  const handleEdit = intent_domains => {

    setActiveIntentDomain(intent_domains)
    setIsModalOpen(true)
  }

  const handleSave = async () => {
    if (!activeIntentDomain?.name?.trim()) return

    try {
      setLoading(true)

      if (activeIntentDomain.id) {
        await IntentDomainAPI.update(activeIntentDomain)
      } else {
        await IntentDomainAPI.create(activeIntentDomain)
      }

      await loadIntentDomain()
      setIsModalOpen(false)
      setActiveIntentDomain(null)
    } catch (err) {
      console.error("Save failed", err)
    } finally {
      setLoading(false)
    }
  }


  /* ================= DELETE ================= */

  const handleDelete = intent_domains => {
    setIntentDomainToDelete(intent_domains)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!intentDomainToDelete) return

    try {
      setLoading(true)
      await IntentDomainAPI.remove(intentDomainToDelete.id)
      setIntentDomain(prev =>
        prev.filter(c => c.id !== intentDomainToDelete.id)
      )
    } catch (err) {
      console.error("Delete failed", err)
    } finally {
      setShowDeleteModal(false)
      setIntentDomainToDelete(null)
      setLoading(false)
    }
  }

  const handleTrain = async domain => {
    try {
      setLoading(true)
      await IntentDomainAPI.train(domain.id)
      alert(`Training started for ${domain.name}`)
    } catch (err) {
      console.error("Training failed", err)
      alert("Training failed")
    } finally {
      setLoading(false)
    }
  }

  // Rebuild the ONE shared model from ALL domains' intents. Use this (not the
  // per-domain Train) when every domain serves from a single combined model —
  // per-domain training would hot-swap the shared model and break the others.
  const [trainingAll, setTrainingAll] = useState(false)
  const handleTrainAll = async () => {
    if (!window.confirm(
      "Rebuild the combined model from ALL domains' intents?\n\n" +
      "This picks up every intent you've added and retrains the single shared " +
      "model. It takes a few minutes and the bot is briefly offline while it " +
      "reloads. Continue?"
    )) return
    try {
      setTrainingAll(true)
      await IntentDomainAPI.trainAll()
      // Poll status until it finishes (or ~10 min safety cap).
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 10000))
        let st
        try { st = await IntentDomainAPI.trainAllStatus() } catch { continue }
        if (st?.status === "SUCCESS") {
          alert(`Combined model retrained successfully (${st.intent_count} intents)` +
                (st.reload_ok ? " and reloaded." : ", but reload failed — restart the demo Rasa server."))
          return
        }
        if (st?.status === "FAILED") {
          alert(`Training failed: ${st.error || "see server logs"}`)
          return
        }
      }
      alert("Training is still running — check back shortly (it may be a slow machine).")
    } catch (err) {
      console.error("Combined training failed to start", err)
      alert("Could not start combined training. Is the NLP service running?")
    } finally {
      setTrainingAll(false)
    }
  }


  /* ================= UI ================= */

  return (
    <div className="p-4 h-100 d-flex flex-column gap-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center">
        <h1 className="fw-bold mb-0">Domains</h1>

        <div className="d-flex gap-2">
          <button
            className="btn btn-success"
            onClick={handleTrainAll}
            disabled={trainingAll || loading}
            title="Rebuild the shared model from ALL domains' intents (use this instead of per-domain Train for the combined-model setup)"
          >
            {trainingAll
              ? <><span className="spinner-border spinner-border-sm me-1" /> Training…</>
              : <><i className="bi bi-cpu me-1" /> Train All (Combined Model)</>}
          </button>
          <button
            className="btn btn-primary"
            onClick={handleAdd}
            disabled={loading}
          >
            <i className="bi bi-plus-lg me-1" />
            Add Domains
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-4 shadow overflow-auto">
        <table className="table mb-0 table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th style={{ width: "60px" }}>S.No</th>
              <th>Name</th>
              <th>Description</th>
              <th>Last Modified</th>
              <th className="text-end">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="5" className="text-center py-4">
                  Loading...
                </td>
              </tr>
            ) : intentDomain.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  No intent Domains found
                </td>
              </tr>
            ) : (
              intentDomain.map((cat, index) => (
                <tr key={cat.id}>
                  <td className="fw-semibold text-muted">
                    {index + 1}
                  </td>

                  <td className="fw-semibold">
                    {cat.name}
                  </td>

                  <td className="Description">
                    {cat.description}
                  </td>

                  <td>
                    {cat.last_modified
                      ? formatDateTime(cat.last_modified)
                      : cat.lastModified}
                  </td>

                  <td className="text-end">
                    <div className="d-flex gap-4 justify-content-end">
                      <Brain
                        size={18}
                        className="text-primary cursor-pointer"
                        title="Train Model"
                        onClick={() => handleTrain(cat)}
                      />
                      <Edit2
                        size={16}
                        className="cursor-pointer"
                        onClick={() => handleEdit(cat)}
                      />
                      <Trash2
                        size={16}
                        className="text-danger cursor-pointer"
                        onClick={() => handleDelete(cat)}
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ADD / EDIT MODAL */}
      {isModalOpen && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content rounded-4 shadow-lg">
                <div className="modal-header border-0">
                  <h5 className="fw-semibold">
                    {activeIntentDomain?.id
                      ? "Edit Intent Domains"
                      : "Add Intent Domains"}
                  </h5>
                  <button
                    className="btn-close"
                    onClick={() => setIsModalOpen(false)}
                  />
                </div>

                <div className="modal-body">
                  <input
                    className="form-control mb-3"
                    placeholder="Name"
                    value={activeIntentDomain?.name || ""}
                    onChange={e =>
                      setActiveIntentDomain(prev => ({
                        ...prev,
                        name: e.target.value
                      }))
                    }
                  />

                  <textarea
                    className="form-control mb-3"
                    rows={3}
                    placeholder="Description"
                    value={activeIntentDomain?.description || ""}
                    onChange={e =>
                      setActiveIntentDomain(prev => ({
                        ...prev,
                        description: e.target.value
                      }))
                    }
                  />
                  <input
                    className="form-control mb-3"
                    placeholder="Rasa Endpoint (http://localhost:8503)"
                    value={activeIntentDomain?.rasa_endpoint || ""}
                    onChange={e =>
                      setActiveIntentDomain(prev => ({
                        ...prev,
                        rasa_endpoint: e.target.value
                      }))
                    }
                  />
                  <div className="form-check mb-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={activeIntentDomain?.auto_reload_enabled || false}
                      onChange={e =>
                        setActiveIntentDomain(prev => ({
                          ...prev,
                          auto_reload_enabled: e.target.checked
                        }))
                      }
                    />
                    <label className="form-check-label">
                      Auto Reload Model
                    </label>
                  </div>


                  {/* Confidence Threshold */}
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-semibold small text-secondary">
                      Confidence Threshold ({activeIntentDomain?.confidence_threshold ?? 60}%)
                    </Form.Label>

                    <Form.Range
                      min={0}
                      max={100}
                      value={activeIntentDomain?.confidence_threshold ?? 60}
                      onChange={e =>
                        setActiveIntentDomain(prev => ({
                          ...prev,
                          confidence_threshold: Number(e.target.value)
                        }))
                      }
                    />

                    <ProgressBar
                      now={activeIntentDomain?.confidence_threshold ?? 60}
                      variant={
                        (activeIntentDomain?.confidence_threshold ?? 60) < 60
                          ? "danger"
                          : "success"
                      }
                    />
                  </Form.Group>



                </div>

                <div className="modal-footer border-0">
                  <button
                    className="btn btn-light"
                    onClick={() => setIsModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={loading}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* DELETE CONFIRMATION */}
      {showDeleteModal && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-dialog-centered modal-sm">
              <div className="modal-content rounded-4">
                <div className="modal-body text-center">
                  <p>
                    <strong>{intentDomainToDelete?.name}</strong>{" "}
                    will be deleted.
                  </p>

                  <div className="d-flex justify-content-center gap-3">
                    <button
                      className="btn btn-light"
                      onClick={() => setShowDeleteModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={confirmDelete}
                      disabled={loading}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default IntentDomainContainer;
